/**
 * Admin console → WhatsApp briefs.
 *
 * The brief agent talks with project-less clients on WhatsApp, composes
 * their brief and posts the project when they approve. This page is the
 * admin's window on it: the mode switch (off / pilot / on), the clients it
 * could be started for, every session with its transcript, and a one-tap
 * "Take over" that stops the agent and hands the thread to a person.
 *
 * "Post for the client" is how a person finishes a handed-off brief: it
 * creates the project under the client's account from the session's facts,
 * notifies contractors and sends them the link. Before the hand-off the
 * client's own "Post it" tap is the approval, so the button is not offered.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Divider, Link, MenuItem,
  Paper, Select, Stack, Typography, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { PlayArrow, PanTool, WhatsApp, Refresh, Send } from '@mui/icons-material';
import adminService from '../services/adminService';
import { panelSx, panelHeaderSx } from '../components/admin/surfaces';
import { brand, status as statusTokens, text, bg, border } from '../theme/colors';

const STATUS = {
  collecting: { label: 'Collecting', tone: statusTokens.info },
  awaiting_confirmation: { label: 'Awaiting client OK', tone: statusTokens.warning },
  posted: { label: 'Posted', tone: statusTokens.success },
  handed_off: { label: 'With a person', tone: statusTokens.neutral },
  cancelled: { label: 'Cancelled', tone: statusTokens.neutral },
};
const MODE_HELP = {
  off: 'The agent never replies. A client who writes in creates the usual "write the brief" task for admins.',
  pilot: 'The agent only talks to clients you start it for below. Everyone else gets the admin task.',
  on: 'The agent talks to every client with no project who writes to the WhatsApp line.',
};

const when = (v) => (v ? new Date(v).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');

export default function AdminWhatsAppBriefsPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('off');
  const [sessions, setSessions] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState('live');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [s, list, cands] = await Promise.all([
        adminService.getWhatsAppBriefSettings(),
        adminService.listWhatsAppBriefSessions(),
        adminService.getWhatsAppBriefCandidates(),
      ]);
      setMode(s.mode);
      setSessions(list);
      setCandidates(cands);
      setSelected((cur) => (cur ? list.find((x) => x.id === cur.id) || null : null));
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changeMode = async (next) => {
    if (!next || next === mode) return;
    setBusy('mode');
    try {
      const r = await adminService.setWhatsAppBriefMode(next);
      setMode(r.mode);
      setNotice(`Agent mode is now "${r.mode}".`);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(''); }
  };

  const start = async (c) => {
    setBusy(`start:${c.id}`);
    setError('');
    try {
      await adminService.startWhatsAppBrief(c.id);
      setNotice(`Started for ${c.clientName}. The agent has sent its first message.`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(''); }
  };

  const takeover = async (s) => {
    setBusy(`take:${s.id}`);
    setError('');
    try {
      await adminService.takeOverWhatsAppBrief(s.id);
      setNotice(`The agent has stopped for ${s.clientName}. A "write the brief" task with the transcript is in your notifications.`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(''); }
  };

  const [outreach, setOutreach] = useState(null);
  const previewOutreach = async () => {
    setBusy('outreach:preview');
    setError('');
    try { setOutreach(await adminService.previewWhatsAppBriefOutreach()); } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(''); }
  };
  const sendOutreach = async () => {
    if (!outreach || !outreach.eligible.length) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Send the WhatsApp question to ${outreach.eligible.length} client${outreach.eligible.length === 1 ? '' : 's'} now? Each gets one message; the agent takes it from there.`)) return;
    setBusy('outreach:send');
    setError('');
    try {
      const r = await adminService.runWhatsAppBriefOutreach();
      setNotice(`Asked ${r.sent} client${r.sent === 1 ? '' : 's'} on WhatsApp${r.failed.length ? `; ${r.failed.length} failed (${r.failed.map((f) => f.name).join(', ')})` : ''}.`);
      setOutreach(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(''); }
  };

  const recompose = async (s) => {
    setBusy(`redo:${s.id}`);
    setError('');
    try {
      await adminService.recomposeWhatsAppBrief(s.id);
      setNotice(`The agent re-read the conversation with ${s.clientName} and sent a corrected brief (or its next question).`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(''); }
  };

  // After a hand-off there was no way to turn the conversation into a
  // project. This posts it under the client's account from what we have.
  const postForClient = async (s) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Post this project for ${s.clientName} now? It goes live under their account, contractors are notified, and they get the link on WhatsApp.`)) return;
    setBusy(`post:${s.id}`);
    setError('');
    try {
      const posted = await adminService.postWhatsAppBriefForClient(s.id);
      setNotice(`Posted ${posted.project_id} for ${s.clientName}. They have the link on WhatsApp.`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(''); }
  };

  const shown = sessions.filter((s) => (filter === 'live'
    ? ['collecting', 'awaiting_confirmation'].includes(s.status)
    : filter === 'all' || s.status === filter));

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
        <WhatsApp sx={{ color: brand.primary }} />
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: text.primary }}>WhatsApp briefs</Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<Refresh />} onClick={load} sx={{ textTransform: 'none' }}>Refresh</Button>
      </Stack>
      <Typography variant="body2" sx={{ color: text.secondary, mb: 2.5 }}>
        The agent writes a client's brief with them on WhatsApp and posts the project when they approve. Nothing is posted without the client's yes.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

      <Paper elevation={0} sx={{ ...panelSx, mb: 2.5 }}>
        <Box sx={panelHeaderSx}><Typography sx={{ fontWeight: 600, fontSize: 14 }}>Agent mode</Typography></Box>
        <Box sx={{ p: 2.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup exclusive size="small" value={mode} onChange={(_e, v) => changeMode(v)} disabled={busy === 'mode'}>
            <ToggleButton value="off" sx={{ textTransform: 'none' }}>Off</ToggleButton>
            <ToggleButton value="pilot" sx={{ textTransform: 'none' }}>Pilot</ToggleButton>
            <ToggleButton value="on" sx={{ textTransform: 'none' }}>On for everyone</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body2" sx={{ color: text.secondary }}>{MODE_HELP[mode]}</Typography>
        </Box>
      </Paper>

      {loading ? <CircularProgress size={28} /> : (
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2.5} alignItems="flex-start">
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Paper elevation={0} sx={{ ...panelSx, mb: 2.5 }}>
              <Box sx={panelHeaderSx}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Clients waiting for a brief ({candidates.length})</Typography>
              </Box>
              {candidates.length === 0 ? (
                <Typography variant="body2" sx={{ p: 2.5, color: text.tertiary }}>Nobody is waiting. Clients appear here once they write to the WhatsApp line with no project yet.</Typography>
              ) : candidates.map((c) => (
                <Box key={c.id} sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${border.light}`, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 260 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{c.clientName} <Typography component="span" variant="caption" sx={{ color: text.tertiary }}>· {c.email}</Typography></Typography>
                    <Typography variant="body2" sx={{ color: text.secondary, whiteSpace: 'pre-wrap' }}>
                      {c.intake_text ? `"${c.intake_text.slice(0, 220)}${c.intake_text.length > 220 ? '…' : ''}"` : 'No note yet — wrote a greeting only.'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: text.tertiary }}>Last message {when(c.last_inbound_at)}</Typography>
                  </Box>
                  <Button size="small" variant="contained" startIcon={<PlayArrow />} disabled={busy === `start:${c.id}` || mode === 'off'}
                    onClick={() => start(c)} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                    Start the agent
                  </Button>
                </Box>
              ))}
              {mode === 'off' && candidates.length > 0 && (
                <Typography variant="caption" sx={{ display: 'block', p: 2, color: text.tertiary }}>Switch the mode to Pilot to start the agent for a client.</Typography>
              )}
            </Paper>

            <Paper elevation={0} sx={{ ...panelSx, mb: 2.5 }}>
              <Box sx={{ ...panelHeaderSx, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14, flex: 1 }}>Signed up, never posted</Typography>
                <Button size="small" variant="outlined" disabled={busy === 'outreach:preview'} onClick={previewOutreach} sx={{ textTransform: 'none' }}>
                  Preview who would be asked
                </Button>
                {outreach && outreach.eligible.length > 0 && (
                  <Button size="small" variant="contained" disabled={busy === 'outreach:send' || mode !== 'on'} onClick={sendOutreach} sx={{ textTransform: 'none' }}>
                    Ask {outreach.eligible.length} on WhatsApp
                  </Button>
                )}
              </Box>
              <Box sx={{ px: 2.5, py: 1.5 }}>
                <Typography variant="body2" sx={{ color: text.secondary }}>
                  Every morning at 09:00 the agent asks every client who signed up more than a day ago with a phone number and never posted a project — however long ago they joined — what they want built. One message each, ever. Silence gets one reminder after 12 hours, then the session closes quietly. Use the buttons to run it now for the backlog.
                </Typography>
                {outreach && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {outreach.eligible.length} would be asked · {outreach.skipped.length} skipped
                      {mode !== 'on' && ' · switch the mode to "On for everyone" to send'}
                    </Typography>
                    {outreach.eligible.map((c) => (
                      <Typography key={c.id} variant="body2" sx={{ color: text.secondary }}>
                        {c.name} · {c.email} · +{c.to} · signed up {when(c.signedUpAt)}{c.linked ? ' · already on WhatsApp' : ''}
                      </Typography>
                    ))}
                    {outreach.skipped.length > 0 && (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: text.tertiary }}>
                        Skipped: {outreach.skipped.map((s) => `${s.name} (${s.reason.replace(/_/g, ' ')}${s.phone ? `: ${s.phone}` : ''})`).join('; ')}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Paper>

            <Paper elevation={0} sx={panelSx}>
              <Box sx={{ ...panelHeaderSx, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Sessions</Typography>
                <Select size="small" value={filter} onChange={(e) => setFilter(e.target.value)} sx={{ ml: 'auto', minWidth: 170, fontSize: 13 }}>
                  <MenuItem value="live">Live</MenuItem>
                  <MenuItem value="posted">Posted</MenuItem>
                  <MenuItem value="handed_off">With a person</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </Box>
              {shown.length === 0 ? (
                <Typography variant="body2" sx={{ p: 2.5, color: text.tertiary }}>No sessions here.</Typography>
              ) : shown.map((s) => {
                const st = s.awaitingFirstReply
                  ? { label: 'Asked, no reply yet', tone: statusTokens.neutral }
                  : STATUS[s.status] || { label: s.status, tone: statusTokens.neutral };
                return (
                  <Box key={s.id} onClick={() => setSelected(s)}
                    sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${border.light}`, cursor: 'pointer', bgcolor: selected?.id === s.id ? bg.subtle : 'transparent', '&:hover': { bgcolor: bg.subtle } }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontWeight: 600, fontSize: 14, flex: 1, minWidth: 0 }}>{s.title || s.clientName}</Typography>
                      <Chip size="small" label={st.label} sx={{ fontSize: 11, height: 22, fontWeight: 600, color: st.tone, bgcolor: `${st.tone}1a` }} />
                    </Stack>
                    <Typography variant="caption" sx={{ color: text.tertiary }}>
                      {s.clientName} · {s.messageCount} messages · {s.agent_turns} agent turns · updated {when(s.updated_at)}
                    </Typography>
                  </Box>
                );
              })}
            </Paper>
          </Box>

          <Paper elevation={0} sx={{ ...panelSx, width: { xs: '100%', lg: 440 }, flexShrink: 0 }}>
            <Box sx={{ ...panelHeaderSx, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14, flex: 1 }}>{selected ? selected.clientName : 'Transcript'}</Typography>
              {selected && ['collecting', 'awaiting_confirmation'].includes(selected.status) && (
                <>
                  <Button size="small" variant="outlined" startIcon={<Refresh />} disabled={busy === `redo:${selected.id}`}
                    onClick={() => recompose(selected)} sx={{ textTransform: 'none' }}
                    title="Re-read the whole conversation and send a corrected brief">
                    Redo brief
                  </Button>
                  <Button size="small" color="warning" variant="outlined" startIcon={<PanTool />} disabled={busy === `take:${selected.id}`}
                    onClick={() => takeover(selected)} sx={{ textTransform: 'none' }}>
                    Take over
                  </Button>
                </>
              )}
              {/* Only after a hand-off: while the agent still has the thread
                  the client's own "Post it" tap is the approval. */}
              {selected && selected.status === 'handed_off' && (
                <Button size="small" variant="contained" startIcon={<Send />} disabled={busy === `post:${selected.id}`}
                  onClick={() => postForClient(selected)} sx={{ textTransform: 'none' }}
                  title="Create the project under the client's account from this brief and tell them on WhatsApp">
                  Post for the client
                </Button>
              )}
            </Box>
            {!selected ? (
              <Typography variant="body2" sx={{ p: 2.5, color: text.tertiary }}>Pick a session to read the conversation.</Typography>
            ) : (
              <Box sx={{ p: 2 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                  <Chip size="small" label={(STATUS[selected.status] || {}).label || selected.status} />
                  {selected.project_id && (
                    <Link component="button" type="button" underline="hover" onClick={() => navigate(`/admin/home/projects/${selected.project_id}`)} sx={{ fontSize: 13 }}>
                      Open project {selected.project_id}
                    </Link>
                  )}
                  <Link component="button" type="button" underline="hover" onClick={() => navigate(`/admin/home/users/${selected.user_id}`)} sx={{ fontSize: 13 }}>
                    Client record
                  </Link>
                </Stack>
                {selected.handoff_reason && (
                  <Alert severity="info" sx={{ mb: 1.5 }}>Handed to a person: {selected.handoff_reason.replace(/_/g, ' ')}</Alert>
                )}
                {selected.brief && (
                  <Box sx={{ mb: 1.5, p: 1.5, borderRadius: 1.5, bgcolor: bg.subtle, border: `1px solid ${border.light}` }}>
                    <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>Brief</Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{selected.title}</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selected.brief}</Typography>
                  </Box>
                )}
                <Typography variant="caption" sx={{ color: text.tertiary, display: 'block', mb: 0.5 }}>
                  Facts so far: {Object.entries(selected.facts || {}).filter(([k]) => !k.startsWith('_')).map(([k, v]) => `${k}=${v}`).join(' · ') || 'none yet'}
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={1} sx={{ maxHeight: 520, overflowY: 'auto' }}>
                  {(selected.transcript || []).map((m, i) => (
                    <Box key={i} sx={{
                      alignSelf: m.role === 'client' ? 'flex-start' : 'flex-end', maxWidth: '88%',
                      px: 1.5, py: 1, borderRadius: 2,
                      bgcolor: m.role === 'client' ? bg.subtle : `${brand.primary}14`,
                    }}>
                      <Typography variant="caption" sx={{ color: text.tertiary, display: 'block' }}>
                        {m.role === 'client' ? 'Client' : 'Agent'} · {when(m.at)}{m.seeded ? ' · from earlier note' : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.text}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Paper>
        </Stack>
      )}
    </Container>
  );
}
